import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isWebMode } from "@/shared/api";
import { BottomSheet } from "@/shared/ui/BottomSheet";
import { useUnread } from "@/shared/unread/UnreadProvider";
import {
  BadgeConteggio,
  NOMI_GRUPPO,
  navItems,
  nomeVoce,
  type GruppoNav,
  type NavItem,
} from "./Rail";
import { IC_CLASS } from "./RailButton";
import { RailLogoutButton } from "./RailLogoutButton";
import { IconaTema } from "./TemaButton";
import { useTheme } from "@/shared/useTheme";
import { useFattureDaFare } from "./useFattureDaFare";

// Le quattro voci sotto il pollice (redesign telefono 2026-10-23): quello che
// si fa fuori ufficio — ordini da evadere, fatture del giorno, clienti che
// scrivono, ritiri da prenotare entro le 11:50. Il resto sta in «Altro».
// Unica fonte resta `navItems` (Rail.tsx): qui si sceglie per path.
const PRIMARY_PATHS = ["/ordini", "/fatturazione", "/messaggi", "/ritiri"] as const;

function byPath(path: string): NavItem | undefined {
  return navItems.find((i) => i.path === path);
}

const primaryItems = PRIMARY_PATHS.map(byPath).filter((i): i is NavItem => Boolean(i));
// Le sotto-voci (Corrispettivi Amazon) nel foglio come voci normali: sul
// telefono non c'e' un sotto-menu che si apre di fianco.
const moreItems: NavItem[] = navItems.flatMap((i) => {
  // Voce con sotto-menu fuori dalla barra (Anagrafiche, Impostazioni): nel
  // foglio vanno le pagine vere, una per riquadro.
  if (!PRIMARY_PATHS.includes(i.path as (typeof PRIMARY_PATHS)[number])) return i.children ?? [i];
  return (i.children ?? []).filter((c) => c.path !== i.path);
});

const GRUPPI_FOGLIO: GruppoNav[] = ["lavoro", "anagrafiche", "numeri", "strumenti"];

function isActivePath(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(path + "/");
}

/** «Corrispettivi Amazon» e' figlio di Fatture: la voce madre non si accende
 *  quando sei sul figlio, che sta nel foglio «Altro». */
function voceAttiva(pathname: string, item: NavItem): boolean {
  if (!isActivePath(pathname, item.path)) return false;
  return !(item.children ?? []).some((c) => c.path !== item.path && isActivePath(pathname, c.path));
}

function MoreIcon() {
  return (
    <svg className={IC_CLASS} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="4" width="6" height="6" rx="1.5" />
      <rect x="14" y="4" width="6" height="6" rx="1.5" />
      <rect x="4" y="14" width="6" height="6" rx="1.5" />
      <rect x="14" y="14" width="6" height="6" rx="1.5" />
    </svg>
  );
}

function TastoNav({
  label,
  icon,
  active,
  badge,
  badgeIntero = false,
  onClick,
  ariaExtra,
}: {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  badge: number;
  badgeIntero?: boolean;
  onClick: () => void;
  ariaExtra?: { "aria-haspopup"?: "dialog"; "aria-expanded"?: boolean };
}) {
  return (
    <button
      type="button"
      aria-label={badge > 0 ? `${label}, ${badge} da vedere` : label}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      {...ariaExtra}
      className="om-press group relative flex min-h-[64px] flex-1 flex-col items-center justify-center gap-1 focus-visible:outline-none"
    >
      {/* L'indicatore a pillola dietro l'icona dice «sei qui» senza colorare
          il testo: le etichette restano nere (regola del testo sempre nero). */}
      <span
        className={[
          "relative grid h-8 w-14 place-items-center rounded-full transition-colors duration-200",
          active
            ? "bg-accent-soft text-ink"
            : "text-ink group-focus-visible:ring-2 group-focus-visible:ring-accent",
        ].join(" ")}
      >
        {icon}
        <BadgeConteggio n={badge} intero={badgeIntero} />
      </span>
      <span className={`text-[12px] leading-none text-ink ${active ? "font-bold" : "font-medium"}`}>
        {label}
      </span>
    </button>
  );
}

/**
 * Barra in basso del telefono: quattro voci + «Altro», alta 64px come le barre
 * di Android, appoggiata sopra la barra dei gesti (safe-area). Montata solo su
 * telefono (AppShell sceglie fra Rail e BottomNav), quindi il poller del
 * conteggio fatture gira una volta sola.
 */
export function BottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { unreadThreads } = useUnread();
  const fattureDaFare = useFattureDaFare();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  const moreActive = moreItems.some((i) => isActivePath(pathname, i.path));

  return (
    <>
      <nav
        aria-label="Navigazione principale"
        className="flex shrink-0 items-stretch border-t border-line bg-card pb-[env(safe-area-inset-bottom)]"
      >
        {primaryItems.map((item) => (
          <TastoNav
            key={item.path}
            label={item.breve ?? item.label}
            icon={item.icon}
            active={voceAttiva(pathname, item)}
            badge={
              item.path === "/messaggi"
                ? unreadThreads
                : item.path === "/fatturazione"
                  ? fattureDaFare
                  : 0
            }
            badgeIntero={item.path === "/fatturazione"}
            onClick={() => void navigate(item.path)}
          />
        ))}
        <TastoNav
          label="Altro"
          icon={<MoreIcon />}
          active={moreOpen || moreActive}
          badge={0}
          onClick={() => setMoreOpen((v) => !v)}
          ariaExtra={{ "aria-haspopup": "dialog", "aria-expanded": moreOpen }}
        />
      </nav>

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title="Altre sezioni">
        {/* Stessi gruppi del rail: la voce si trova dalla sua zona. */}
        {GRUPPI_FOGLIO.map((gruppo) => {
          const voci = moreItems.filter((i) => i.gruppo === gruppo);
          if (voci.length === 0) return null;
          return (
            <section key={gruppo} aria-label={NOMI_GRUPPO[gruppo]} className="pt-1 [&+&]:pt-4">
              <h3 className="pb-2 text-[13px] font-bold text-ink">{NOMI_GRUPPO[gruppo]}</h3>
              <ul className="grid grid-cols-3 gap-2">
                {voci.map((item) => {
                  const attiva = isActivePath(pathname, item.path);
                  return (
                    <li key={item.path}>
                      <button
                        type="button"
                        aria-current={attiva ? "page" : undefined}
                        onClick={() => void navigate(item.path)}
                        className={[
                          "om-press flex h-[84px] w-full flex-col items-center justify-center gap-2 rounded-2xl border px-2 text-center",
                          attiva
                            ? "border-accent bg-accent-soft"
                            : "border-line bg-card active:bg-paper-2",
                        ].join(" ")}
                      >
                        <span className="text-ink">{item.icon}</span>
                        <span className="text-[13px] font-semibold leading-tight text-ink">
                          {nomeVoce(item)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
        <TemaRiga />
        {/* Fuori da web-mode (dev) non c'e' sessione da chiudere. */}
        {isWebMode() && (
          <div className="mt-4 flex justify-center rounded-2xl bg-rail py-2">
            <RailLogoutButton />
          </div>
        )}
      </BottomSheet>
    </>
  );
}

/** Il tasto del tema sul telefono: una riga intera, da pollice. */
function TemaRiga() {
  const { theme, toggle } = useTheme();
  const scuro = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={scuro}
      className="om-press mt-3 flex h-14 w-full items-center gap-3 rounded-2xl border border-line bg-card px-4 text-left text-[15px] font-semibold text-ink active:bg-paper-2"
    >
      <IconaTema
        scuro={scuro}
        className="h-6 w-6 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]"
      />
      <span className="flex-1">{scuro ? "Passa al tema chiaro" : "Passa al tema scuro"}</span>
    </button>
  );
}
